<script>
import KpiScalarBox from './partials/KpiScalarBox.vue';
import KpiCompositeGrid from './partials/KpiCompositeGrid.vue';
import DashboardApexChart from './DashboardApexChart.vue';
import dashboardKpiApi from './mixins/dashboardKpiApi';
import dashboardPeriodToolbar from './mixins/dashboardPeriodToolbar';
import dashboardNumberFormat from './mixins/dashboardNumberFormat';

export default {
    name: 'KpiViewer',
    components: {
        KpiScalarBox,
        KpiCompositeGrid,
        DashboardApexChart,
    },
    mixins: [dashboardKpiApi, dashboardPeriodToolbar, dashboardNumberFormat],
    props: {
        autoFetch: {
            type: Boolean,
            default: false,
        },
        composite: {
            type: Boolean,
            default: false,
        },
        chartable: {
            type: Boolean,
            default: false,
        },
        chartType: {
            type: String,
            default: 'line',
        },
        heroBoxColor: {
            type: String,
            default: 'bg-purple',
        },
        heroIcon: {
            type: String,
            default: 'fa-line-chart',
        },
        metricPalette: {
            type: Array,
            default: () => ['bg-aqua', 'bg-teal', 'bg-maroon'],
        },
        reserveRevenue: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        showCompositeLayout() {
            return this.composite || this.kpiMetrics.length > 0;
        },
        revenueMetrics() {
            return this.kpiPayload?.meta?.revenue_metrics ?? [];
        },
        hasRevenueSection() {
            return this.revenueMetrics.length > 0;
        },
        showRevenueSection() {
            return this.hasRevenueSection || (this.reserveRevenue && !this.kpiPayload);
        },
        heroMetricKey() {
            return this.kpiPayload?.hero_metric?.key ?? '';
        },
        revenueSlots() {
            if (this.revenueMetrics.length > 0) {
                return this.revenueMetrics;
            }

            return [
                { key: 'revenue-slot-0', placeholder: true },
                { key: 'revenue-slot-1', placeholder: true },
            ];
        },
    },
    mounted() {
        if (!this.from && !this.to && this.preset !== 'all') {
            this.setPresetDates(this.preset);
        }

        this.fetchKpi(this.buildPeriodParams());
    },
};
</script>

<template>
    <div class="kpi-viewer">
        <div class="kpi-viewer__toolbar">
            <select
                v-model="preset"
                class="form-control input-sm kpi-viewer__preset"
                @change="onPresetChange"
            >
                <option value="7">{{ presetLabels.last7 }}</option>
                <option value="30">{{ presetLabels.last30 }}</option>
                <option value="90">{{ presetLabels.last90 }}</option>
                <option value="year">{{ presetLabels.year }}</option>
                <option value="all">{{ presetLabels.all }}</option>
            </select>
            <button
                type="button"
                class="btn btn-default btn-sm kpi-viewer__refresh"
                :disabled="kpiLoading"
                @click="refreshPeriod"
            >
                <i class="fa" :class="kpiLoading ? 'fa-spinner fa-spin' : 'fa-refresh'" />
            </button>
        </div>
        <div v-if="kpiError" class="kpi-viewer__error alert alert-danger">{{ kpiError }}</div>
        <div class="kpi-stage" :aria-busy="kpiLoading ? 'true' : 'false'">
            <div v-if="showRevenueSection" class="kpi-viewer__revenue">
                <h4 class="kpi-viewer__section-title">{{ _t('Revenue') }}</h4>
                <div class="row kpi-summary kpi-summary--revenue">
                    <div
                        v-for="(metric, index) in revenueSlots"
                        :key="metric.key"
                        class="col-sm-6"
                    >
                        <div
                            class="small-box"
                            :class="metricPalette[index % metricPalette.length] || 'bg-teal'"
                        >
                            <div class="inner">
                                <h3>{{ metric.placeholder ? '\u00a0' : formatKpiValue(metric.value, metric.key) }}</h3>
                                <p>{{ metric.placeholder ? '\u00a0' : metric.label }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <h4 v-if="showRevenueSection" class="kpi-viewer__section-title">{{ _t('Orders') }}</h4>
            <kpi-composite-grid
                v-if="showCompositeLayout"
                :hero-value="kpiHeroValue"
                :hero-label="kpiPayload?.hero_metric?.label || '\u00a0'"
                :hero-key="heroMetricKey"
                :metrics="kpiMetrics"
                :hero-box-color="heroBoxColor"
                :hero-icon="heroIcon"
                :metric-palette="metricPalette"
            />
            <kpi-scalar-box
                v-else
                :value="kpiHeroValue"
                :label="kpiPayload?.hero_metric?.label || '\u00a0'"
                :metric-key="heroMetricKey"
                :box-color="heroBoxColor"
                :icon="heroIcon"
            />
            <div v-if="chartable" class="box box-primary dashboard-chart-panel">
                <div class="box-header with-border">
                    <h3 class="box-title">{{ _t('Trend') }}</h3>
                </div>
                <div class="box-body">
                    <dashboard-apex-chart
                        v-if="kpiChart"
                        :chart="kpiChart"
                        :chart-type="chartType"
                    />
                    <div v-else class="dashboard-apex-chart" aria-hidden="true" />
                </div>
            </div>
            <div v-if="kpiLoading" class="kpi-stage__overlay">
                <div class="kpi-stage__overlay-inner">
                    <i class="fa fa-spinner fa-spin" aria-hidden="true" />
                    <span class="kpi-stage__overlay-label">{{ _t('Loading...') }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.kpi-viewer__section-title {
    margin: 0 0 10px;
    font-size: 14px;
    font-weight: 600;
    color: #555;
}
.kpi-viewer__revenue {
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #eee;
}
</style>
